import { ChangeDetectionStrategy, Component, input } from '@angular/core';

import { IrdButtonBranding, IrdButtonDirective, IrdButtonSize, IrdButtonVariant } from '../shared/ird-button.directive';

@Component({
  selector: 'ird-button',
  standalone: true,
  imports: [IrdButtonDirective],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './ird-button.component.html',
  styleUrl: './ird-button.component.scss',
  host: {
    // The inner <button> is `pointer-events: none` while disabled, which makes the
    // browser hit-test straight through to this host — where consumers bind (click).
    // Without blocking the host too, a disabled button still fires its handler.
    '[class.ird-button--host-disabled]': 'disabled() || loading()',
  },
})
export class IrdButtonComponent {
  // Defaults to 'button' so the control never submits an enclosing form unless
  // the caller explicitly opts in via type="submit" / "reset".
  readonly type = input<'button' | 'submit' | 'reset'>('button');
  readonly branding = input<IrdButtonBranding>('universal');
  readonly variant = input<IrdButtonVariant>('primary');
  readonly size = input<IrdButtonSize>('md');
  readonly disabled = input(false);
  readonly loading = input(false);
  readonly iconOnly = input(false);
  /** Accessible name for the button, forwarded to the inner native `<button>`. */
  readonly ariaLabel = input<string | undefined>(undefined, { alias: 'aria-label' });
  /**
   * Expanded state for buttons that toggle a region, forwarded to the inner native
   * `<button>` — set on the host element it would sit on the `ird-button` wrapper
   * instead of the focusable control, where assistive tech never reads it.
   */
  readonly ariaExpanded = input<boolean | undefined>(undefined, { alias: 'aria-expanded' });
  /** Id of the region this button controls, forwarded to the inner native `<button>`. */
  readonly ariaControls = input<string | undefined>(undefined, { alias: 'aria-controls' });
}
